import React, { useState, useEffect, useMemo } from 'react';
import { Plus, ArrowUpDown, SortAsc, SortDesc } from 'lucide-react';
import { AddOrganizerModal } from '../components/organizers/AddOrganizerModal';
import { OrganizersStats } from '../components/organizers/OrganizersStats';
import { OrganizerFilters } from '../components/organizers/OrganizerFilters';
import { OrganizersList } from '../components/organizers/OrganizersList';
import { OrganizersCardView } from '../components/organizers/OrganizersCardView';
import { useOrganizersStore } from '../stores/organizersStore';
import { OrganizersCountryChart } from '../components/organizers/OrganizersCountryChart';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { motion } from 'framer-motion';
import { SyncOrganizerNamesModal } from '../components/organizers/SyncOrganizerNamesModal';

export const OrganizersPage = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSyncNamesModalOpen, setIsSyncNamesModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [sortField, setSortField] = useState<'name' | 'createdAt' | 'country'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [viewMode, setViewMode] = useState<'list' | 'cards'>(() => {
    // Get view mode from local storage or default to 'cards'
    return (localStorage.getItem('organizer-view-mode') as 'list' | 'cards') || 'cards';
  });

  // Store view mode in local storage
  useEffect(() => {
    localStorage.setItem('organizer-view-mode', viewMode);
  }, [viewMode]);

  const { organizers, loading, fetchOrganizers, syncOrganizerName } = useOrganizersStore();

  // Fetch organizers on mount
  useEffect(() => {
    fetchOrganizers();
  }, [fetchOrganizers]);

  // Get unique countries for filtering
  const countries = useMemo(() => {
    return Array.from(new Set(
      Object.values(organizers)
        .map(org => org.country)
        .filter(Boolean) as string[]
    )).sort();
  }, [organizers]);

  // Filter and sort organizers
  const filteredOrganizers = useMemo(() => {
    return Object.values(organizers)
      .filter(org => {
        const matchesSearch = !searchTerm || 
          org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          org.email.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesCountry = !countryFilter || org.country === countryFilter;

        return matchesSearch && matchesCountry;
      })
      .sort((a, b) => {
        if (sortField === 'name') {
          return sortDirection === 'asc' 
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        } else if (sortField === 'createdAt') {
          return sortDirection === 'asc'
            ? a.createdAt - b.createdAt
            : b.createdAt - a.createdAt;
        } else if (sortField === 'country') {
          const countryA = a.country || '';
          const countryB = b.country || '';
          return sortDirection === 'asc'
            ? countryA.localeCompare(countryB)
            : countryB.localeCompare(countryA);
        }
        return 0;
      });
  }, [organizers, searchTerm, countryFilter, sortField, sortDirection]);

  // Calculate statistics
  const stats = useMemo(() => {
    const allOrganizers = Object.values(organizers);
    const totalOrganizers = allOrganizers.length;
    
    // Count unique countries
    const uniqueCountries = new Set(allOrganizers.map(org => org.country).filter(Boolean));
    
    // Count organizers created in the last 30 days
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    const recentOrganizers = allOrganizers.filter(org => org.createdAt > thirtyDaysAgo).length;
    
    return {
      total: totalOrganizers,
      countriesCount: uniqueCountries.size,
      recent: recentOrganizers
    };
  }, [organizers]);

  // Function to handle sort
  const handleSort = (field: 'name' | 'createdAt' | 'country') => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Function to get sort icon
  const getSortIcon = (field: 'name' | 'createdAt' | 'country') => {
    if (field !== sortField) return null;
    
    return sortDirection === 'asc' 
      ? <SortAsc size={14} className="text-gray-500" />
      : <SortDesc size={14} className="text-gray-500" />;
  };

  const handleSyncName = async (organizerId: string) => {
    await syncOrganizerName(organizerId);
  };

  if (loading && Object.keys(organizers).length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <h1 className="text-2xl font-bold">Organizers</h1>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsSyncNamesModalOpen(true)}
            className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
          >
            Sync Organizer Names
          </button>
          
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-2 px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            <Plus size={20} />
            <span>Add Organizer</span>
          </button>
        </div>
      </div>

      <OrganizersStats stats={stats} />

      <OrganizerFilters
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        countryFilter={countryFilter}
        setCountryFilter={setCountryFilter}
        countries={countries}
        viewMode={viewMode}
        setViewMode={setViewMode}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        getSortIcon={getSortIcon}
      />

      {filteredOrganizers.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white p-8 rounded-lg shadow-sm text-center"
        >
          <p className="text-gray-500">No organizers found matching your filters.</p>
        </motion.div>
      ) : (
        <>
          {viewMode === 'list' ? (
            <OrganizersList onSyncNameClick={handleSyncName} />
          ) : (
            <OrganizersCardView 
              organizers={filteredOrganizers} 
              onSyncNameClick={handleSyncName}
            />
          )}
        </>
      )}

      {/* Only show the country chart if there are organizers with countries */}
      {filteredOrganizers.some(org => org.country) && (
        <OrganizersCountryChart organizers={filteredOrganizers} />
      )}

      {isAddModalOpen && (
        <AddOrganizerModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
        />
      )}

      {isSyncNamesModalOpen && (
        <SyncOrganizerNamesModal
          isOpen={isSyncNamesModalOpen}
          onClose={() => setIsSyncNamesModalOpen(false)}
          organizers={organizers}
        />
      )}
    </div>
  );
};